import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity, AlertTriangle, ArrowRight, Brain, CalendarDays, ClipboardCheck,
  RefreshCw, ShieldAlert, Stethoscope, Users
} from 'lucide-react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useAuth } from '../lib/auth.jsx'
import { supabase } from '../lib/supabase'
import styles from './DashboardPage.module.css'

const EMPTY = {
  patientCount: null,
  appointments: [],
  scaleCount: null,
  alerts: [],
  phqScores: [],
  gadScores: [],
}

const ALERT_LABELS = {
  suicide_risk_item_9: 'Suicide-risk response',
  suicide_risk: 'Suicide-risk response',
  severe_depression: 'Severe depression score',
  severe_anxiety: 'Severe anxiety score',
  significant_increase: 'Clinically significant score increase',
}

function average(rows) {
  if (!rows?.length) return null
  return (rows.reduce((sum, row) => sum + Number(row.total_score || 0), 0) / rows.length).toFixed(1)
}

function useClinicalDashboard(orgId, authLoading) {
  const [snapshot, setSnapshot] = useState(EMPTY)
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState([])

  const load = useCallback(async () => {
    if (authLoading) return
    if (!orgId) {
      setSnapshot(EMPTY)
      setErrors(['organization: No active organization context is available.'])
      setLoading(false)
      return
    }
    setLoading(true)
    setErrors([])

    const today = new Date().toISOString().slice(0, 10)
    const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString()
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString()
    const queries = [
      ['patientCount', supabase.schema('cr').from('patient').select('*', { count: 'exact', head: true }).eq('org_id', orgId)],
      ['appointments', supabase.schema('cr').from('appointments').select('appointment_id, appointment_time, status, appointment_type, patient:patient_id(patient_id, first_name, last_name)').eq('org_id', orgId).eq('appointment_date', today).neq('status', 'cancelled').order('appointment_time').limit(12)],
      ['scaleCount', supabase.schema('cr').from('patient_scale_administrations').select('*', { count: 'exact', head: true }).eq('org_id', orgId).gte('administered_at', thirtyDaysAgo)],
      ['alerts', supabase.schema('cr').from('patient_scale_administrations').select('admin_id, patient_id, scale_code, total_score, severity_label, alert_type, administered_at').eq('org_id', orgId).eq('alert_triggered', true).eq('alert_acknowledged', false).order('administered_at', { ascending: false }).limit(8)],
      ['phqScores', supabase.schema('cr').from('scale_trend').select('administered_at, total_score').eq('org_id', orgId).eq('scale_code', 'PHQ-9').gte('administered_at', ninetyDaysAgo).order('administered_at', { ascending: true }).limit(100)],
      ['gadScores', supabase.schema('cr').from('scale_trend').select('administered_at, total_score').eq('org_id', orgId).eq('scale_code', 'GAD-7').gte('administered_at', ninetyDaysAgo).order('administered_at', { ascending: true }).limit(100)],
    ]

    const results = await Promise.all(queries.map(async ([key, query]) => {
      const response = await query
      return { key, ...response }
    }))

    const next = { ...EMPTY }
    const nextErrors = []
    results.forEach(({ key, data, count, error }) => {
      if (error) {
        nextErrors.push(`${key}: ${error.message}`)
        return
      }
      next[key] = key.endsWith('Count') ? count : (data || [])
    })
    setSnapshot(next)
    setErrors(nextErrors)
    setLoading(false)
  }, [orgId, authLoading])

  useEffect(() => { load() }, [load])
  return { snapshot, loading, errors, reload: load }
}

function scoreTrend(phqScores, gadScores) {
  const points = new Map()
  const add = (rows, key) => rows.forEach((row) => {
    const raw = row.administered_at || row.administered_at
    if (!raw) return
    const dateKey = new Date(raw).toISOString().slice(0, 10)
    const point = points.get(dateKey) || { dateKey, date: new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
    point[key] = Number(row.total_score)
    points.set(dateKey, point)
  })
  add(phqScores, 'phq9')
  add(gadScores, 'gad7')
  return [...points.values()].sort((a, b) => a.dateKey.localeCompare(b.dateKey))
}

function displayTime(value) {
  if (!value) return 'Time unavailable'
  const [hour, minute] = value.split(':').map(Number)
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export default function DashboardPage() {
  const { user, orgId, loading: authLoading } = useAuth()
  const { snapshot, loading, errors, reload } = useClinicalDashboard(orgId, authLoading)
  const trend = useMemo(() => scoreTrend(snapshot.phqScores, snapshot.gadScores), [snapshot.phqScores, snapshot.gadScores])
  const displayName = user?.user_metadata?.given_name || user?.user_metadata?.full_name?.split(' ')[0] || 'Clinician'
  const kpis = [
    { label: 'Active patients', value: snapshot.patientCount, note: 'Verified patient records', icon: Users, to: '/app/patients' },
    { label: "Today's encounters", value: snapshot.appointments.length, note: 'Non-cancelled appointments', icon: CalendarDays, to: '/app/scheduling' },
    { label: 'Scale administrations', value: snapshot.scaleCount, note: 'Verified in the last 30 days', icon: ClipboardCheck, to: '/app/assessments' },
    { label: 'Unacknowledged alerts', value: snapshot.alerts.length, note: 'Review by alert type', icon: ShieldAlert, to: '/app/safety', urgent: snapshot.alerts.length > 0 },
  ]

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroCopy}>
          <span className={styles.eyebrow}>PatientTrac Mind · Clinical command center</span>
          <h1>{displayName}, your neurobehavioral work is in one place.</h1>
          <p>Live operational data for Psychiatry, Behavioral Health, and Neurology. No demonstration patients or fabricated clinical metrics are shown.</p>
          <div className={styles.heroActions}>
            <Link to="/app/clinical" className={styles.primaryAction}><Stethoscope size={17} /> Open Psychiatry</Link>
            <Link to="/app/neurology" className={styles.secondaryAction}><Brain size={17} /> Open Neurology</Link>
          </div>
        </div>
        <img src="/images/mind/psychiatry-neurobiology.png" alt="Clinical neurobiology visualization of the limbic system and cortical pathways" />
      </section>

      <div className={styles.contextBar}>
        <div>
          <strong>Live clinical data</strong>
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
        </div>
        <button onClick={reload} disabled={loading}><RefreshCw size={15} className={loading ? styles.spinning : ''} /> Refresh</button>
      </div>

      {errors.length > 0 && (
        <div className={styles.dataWarning} role="status">
          <AlertTriangle size={18} />
          <div><strong>Some clinical data is unavailable.</strong><span>The dashboard will not substitute sample data. Check the integration notes before release.</span></div>
        </div>
      )}

      <section className={styles.kpiGrid} aria-label="Clinical metrics">
        {kpis.map(({ label, value, note, icon: Icon, to, urgent }) => (
          <Link key={label} to={to} className={`${styles.kpiCard} ${urgent ? styles.kpiUrgent : ''}`}>
            <div className={styles.kpiIcon}><Icon size={20} /></div>
            <div><strong>{loading ? '…' : (value ?? 'Unavailable')}</strong><span>{label}</span><small>{note}</small></div>
            <ArrowRight size={16} />
          </Link>
        ))}
      </section>

      <section className={styles.pillarGrid}>
        <Link to="/app/clinical" className={styles.pillarCard}>
          <img src="/images/mind/behavioral-assessment.png" alt="Clinical behavioral assessment and neural pathway visualization" />
          <div className={styles.pillarOverlay}><span>Clinical pillar</span><h2>Psychiatry & Behavioral Health</h2><p>Encounters, MSE, safety, therapy, medication management, and validated scales.</p><strong>Enter workspace <ArrowRight size={15} /></strong></div>
        </Link>
        <Link to="/app/neurology" className={styles.pillarCard}>
          <img src="/images/mind/neurology-diagnostics.png" alt="Neurologic MRI, EEG and tractography diagnostic visualization" />
          <div className={styles.pillarOverlay}><span>Clinical pillar</span><h2>Neurology</h2><p>Queues, encounters, diagnostic categories, examinations, scales, and documentation actions.</p><strong>Enter workspace <ArrowRight size={15} /></strong></div>
        </Link>
      </section>

      <div className={styles.dashboardGrid}>
        <section className={styles.panel}>
          <header><div><span>Today</span><h2>Encounter queue</h2></div><Link to="/app/scheduling">Full schedule</Link></header>
          {loading ? <div className={styles.empty}>Loading verified encounters…</div> : snapshot.appointments.length ? (
            <div className={styles.encounterList}>{snapshot.appointments.map((appt) => (
              <Link to={appt.patient?.patient_id ? `/app/patients/${appt.patient.patient_id}` : '/app/scheduling'} key={appt.appointment_id} className={styles.encounterRow}>
                <time>{displayTime(appt.appointment_time)}</time>
                <div><strong>{appt.patient ? `${appt.patient.last_name}, ${appt.patient.first_name}` : 'Patient identity unavailable'}</strong><span>{appt.appointment_type || 'Encounter type unavailable'}{/tele/i.test(appt.appointment_type || '') ? ' · TeleMed' : ''}</span></div>
                <span className={styles.status}>{appt.status || 'scheduled'}</span>
              </Link>
            ))}</div>
          ) : <div className={styles.empty}>No non-cancelled encounters returned for today.</div>}
        </section>

        <section className={styles.panel}>
          <header><div><span>Safety</span><h2>Clinical alerts</h2></div><Link to="/app/safety">Safety worklist</Link></header>
          {loading ? <div className={styles.empty}>Loading verified alerts…</div> : snapshot.alerts.length ? (
            <div className={styles.alertList}>{snapshot.alerts.map((alert) => (
              <Link to="/app/safety" key={alert.admin_id || `${alert.patient_id}-${alert.administered_at}`} className={styles.alertRow}>
                <AlertTriangle size={17} />
                <div><strong>{ALERT_LABELS[alert.alert_type] || alert.alert_type?.replaceAll('_', ' ') || 'Clinical threshold alert'}</strong><span>{alert.scale_code || 'Scale'} · score {alert.total_score ?? 'unavailable'} · {alert.severity_label || 'severity unavailable'}</span></div>
              </Link>
            ))}</div>
          ) : <div className={styles.empty}>No unacknowledged scale alerts returned.</div>}
        </section>
      </div>

      <section className={styles.panel}>
        <header><div><span>Measurement-based care</span><h2>PHQ-9 and GAD-7 · 90 days</h2></div><Link to="/app/assessments">Assessment workspace</Link></header>
        <div className={styles.scoreSummary}>
          <div><Activity size={16} /><span>PHQ-9 average</span><strong>{loading ? '…' : (average(snapshot.phqScores) ?? 'No data')}</strong></div>
          <div><Activity size={16} /><span>GAD-7 average</span><strong>{loading ? '…' : (average(snapshot.gadScores) ?? 'No data')}</strong></div>
        </div>
        {trend.length ? (
          <div className={styles.chart}>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trend} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 27]} tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #CBD5E1' }} />
                <Legend />
                <Line name="PHQ-9" type="monotone" dataKey="phq9" connectNulls={false} stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line name="GAD-7" type="monotone" dataKey="gad7" connectNulls={false} stroke="#0F8B8D" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <div className={styles.empty}>No verified PHQ-9 or GAD-7 scores were returned for this period.</div>}
      </section>
    </div>
  )
}
